import datapoints from './datapoints';
import logFlagInstrumentationContexts from './helpers/logFlagInstrumentationContexts';
import { ActionSource, createLegacyActionWithDatapoint } from 'owa-analytics-actions';
import applyConversationFlagInListView from 'owa-mail-list-actions/lib/actions/triage/applyConversationFlag';
import listViewStore from 'owa-mail-list-store/lib/store/Store';
import { getMailboxInfo } from 'owa-mail-mailboxinfo';
import { InstrumentationContext } from 'owa-mail-store';
import { flagConversationService } from 'owa-mail-store/lib/services/flagConversationService';
import flagItemService from 'owa-mail-store/lib/services/flagItemService';
import * as undoActions from 'owa-mail-undo';
import FlagType from 'owa-service/lib/contract/FlagType';
import { raiseAction } from 'satcheljs/lib/legacy/stitch';
import {
    getApplyConversationActionContext,
    getRowIdsFromRowKeys,
    getContextFolderIdForTable,
    canTableBeOutOfSyncWithServer,
    getItemIdsToUpdateForFlagOperation,
    TableView,
} from 'owa-mail-list-store';
import {
    ApplyConversationFlagStateStoreUpdate,
    ApplyConversationFlagStateStoreUpdateActionName,
} from 'owa-mail-store-actions';
import {
    handleServerResponseSuccessAndError,
    processApplyConversationActionResponseMessage,
} from 'owa-mail-triage-action-response-processor';

const SetConversationsFlagStateActionName = 'setConversationsFlagState';

/**
 * Set the specified flag state on a collection of conversations.
 * @param conversationRowKeys - the conversations row keys
 * @param tableViewId - the table view the conversations are in
 * @param flagType - the specified flag type to set
 */
const setConversationsFlagStateInternal = createLegacyActionWithDatapoint(
    SetConversationsFlagStateActionName,
    datapoints.setConversationsFlagState
)(function setConversationsFlagStateInternal(
    conversationRowKeys: string[],
    instrumentationContexts: InstrumentationContext[],
    tableViewId: string,
    flagType: FlagType,
    actionSource: ActionSource
) {
    undoActions.clearLastUndoableAction();
    const tableView = listViewStore.tableViews.get(tableViewId);

    logFlagInstrumentationContexts(
        flagType,
        instrumentationContexts,
        conversationRowKeys,
        tableView
    );

    // 1. Make local updates to update list view store and RP
    const conversationIds = getRowIdsFromRowKeys(conversationRowKeys, tableViewId);
    applyConversationFlagInListView(conversationRowKeys, flagType, tableViewId);
    raiseAction<ApplyConversationFlagStateStoreUpdate>(
        ApplyConversationFlagStateStoreUpdateActionName
    )(conversationIds, flagType, tableViewId);

    // 2. Make service call

    // Perform item level operation if the table can be out of sync with server
    if (canTableBeOutOfSyncWithServer(tableView)) {
        return performItemLevelFlagOperation(tableView, flagType, conversationRowKeys);
    } else {
        return performConversationLevelFlagOperation(tableView, flagType, conversationRowKeys);
    }
});

/**
 * Set the specified flag state on a collection of conversations.
 * @param conversationRowKeys - the conversations row keys
 * @param instrumentationContexts - the instrumentation contexts
 * @param tableViewId - the table view the conversations are in
 * @param flagType - the specified flag type to set
 */
export default function setConversationsFlagState(
    conversationRowKeys: string[],
    instrumentationContexts: InstrumentationContext[],
    tableViewId: string,
    flagType: FlagType,
    actionSource: ActionSource,
    getFilteredRowKeysToPerformFlagAction: (
        rowKeys: string[],
        tableViewId: string,
        flagType: FlagType
    ) => string[],
    override?: boolean
) {
    let rowKeysToPerformActionUpon = [];
    if (override) {
        rowKeysToPerformActionUpon = conversationRowKeys;
    } else {
        const filteredRowKeys = getFilteredRowKeysToPerformFlagAction(
            conversationRowKeys,
            tableViewId,
            flagType
        );

        rowKeysToPerformActionUpon = filteredRowKeys;
    }
    if (rowKeysToPerformActionUpon.length > 0) {
        setConversationsFlagStateInternal(
            rowKeysToPerformActionUpon,
            instrumentationContexts,
            tableViewId,
            flagType,
            actionSource
        );
    }
}

/**
 * Perform item level flag operation by getting itemIds from the rows
 * @param tableView tableView where flag operation is to be performed
 * @param flagType flag type to stamp
 * @param conversationRowKeys rowKeys for conversations
 */
function performItemLevelFlagOperation(
    tableView: TableView,
    flagType: FlagType,
    conversationRowKeys: string[]
): Promise<void> {
    const requestOptions = getMailboxInfo(tableView).requestOptions;
    const itemIds = getItemIdsToUpdateForFlagOperation(flagType, conversationRowKeys, tableView);
    return flagItemService(itemIds, flagType, requestOptions)
        .then(response => {
            return handleServerResponseSuccessAndError(response);
        })
        .catch(error => {
            return Promise.reject(error);
        });
}

/**
 * Perform conversation level flag operation
 * @param tableView tableView where flag operation is to be performed
 * @param flagType flag type to stamp
 * @param conversationRowKeys rowKeys
 */
function performConversationLevelFlagOperation(
    tableView: TableView,
    flagType: FlagType,
    conversationRowKeys: string[]
) {
    const contextFolderId = getContextFolderIdForTable(tableView);
    const conversationActionContexts = conversationRowKeys.map(rowKey =>
        getApplyConversationActionContext(rowKey, tableView.id)
    );

    const flagConversationPromise = flagConversationService(
        conversationActionContexts,
        flagType,
        contextFolderId
    );

    return processApplyConversationActionResponseMessage(
        flagConversationPromise,
        conversationRowKeys,
        contextFolderId,
        tableView,
        false /* shouldRemoveRowsFromTombstone */
    );
}
